// router/index.js
import { createRouter, createWebHistory } from "vue-router";
import ForgotPassword from "../components/ForgotPassword.vue";
import ResetPassword from "../components/ResetPassword.vue";
import Login from "../components/Login.vue";
import Signup from "../components/Signup.vue";
import Terms from "../components/tnc.vue";
import Users from "../components/business/Users.vue";
import EditUser from "../components/business/EditUser.vue";
import InviteUser from "../components/business/InviteUser.vue";
import InviteMany from "../components/business/InviteMany.vue";
import CreateLink from "../components/business/CreateLink.vue";
import BusinessProfile from "../components/business/BusinessProfile.vue";
import InviteLanding from "../components/business/InviteLanding.vue";
import ViewPayments from "../components/transactions/ViewPayments.vue";
import ViewStatement from "../components/transactions/ViewStatement.vue";
import OrderHistory from "../components/transactions/OrderHistory.vue";
import IndividualProfile from "../components/profile/Profile.vue";
import ChangePassword from "../components/profile/Password.vue";
import AccountLayout from "../components/account/AccountLayout.vue";
import KycVerification from "../components/account/KycVerification.vue";
import AccountSettings from "../components/account/AccountSettings.vue";
import OtpSettings from "../components/account/OtpSettings.vue";
import VerifyFlow from "../components/verify/VerifyFlow.vue";
import NewRequest from "../components/order/NewRequest.vue";
import TrackOrder from "../components/order/TrackOrder.vue";
import PublicTrip from "../components/order/PublicTrip.vue";
import RateOrder from "../components/order/RateOrder.vue";
import { isCompanyAdminFromToken } from "../utils/authRole";

/**
 * Paths only company admins (user_type 2) may access. Members (sub-users)
 * place orders and see their history; company verification and the order
 * settings are for admins only, so a member typing those links lands on the
 * order page.
 */
const ADMIN_ONLY_PATH_PREFIXES = [
  "/account/business",
  "/account/team",
  "/account/verification",
  "/account/settings",
  "/verify",
  "/kyc",
  "/users",
  "/business",
  "/add_user",
  "/bulk_invite",
  "/create_link",
  "/edituser",
  "/business-profile",
  "/transactions/statement",
  "/transactions/payments",
];

function isAdminOnlyPath(path: string): boolean {
  const p = path.split("?")[0];
  if (
    ADMIN_ONLY_PATH_PREFIXES.some(
      (prefix) => p === prefix || p.startsWith(prefix + "/"),
    )
  )
    return true;
  return false;
}

const routes = [
  {
    path: "/login",
    name: "Login",
    component: Login,
  },
  {
    path: "/forgot",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  {
    path: "/signup",
    name: "Signup",
    component: Signup,
  },
  // Account section: one layout, a navigation on the left and the page on
  // the right. Verification is open to every user of a business account
  // (admins upload, everyone else can see where it stands); Business profile
  // and Team are admin-only (ADMIN_ONLY_PATH_PREFIXES).
  {
    path: "/account",
    component: AccountLayout,
    children: [
      { path: "", redirect: "/account/profile" },
      { path: "profile", name: "IndividualProfile", component: IndividualProfile },
      { path: "password", name: "ChangePassword", component: ChangePassword },
      { path: "business", name: "BusinessProfile", component: BusinessProfile },
      { path: "team", name: "Users", component: Users },
      { path: "team/invite", name: "InviteUser", component: InviteUser },
      { path: "team/:id/edit", name: "EditUser", component: EditUser },
      { path: "verification", name: "Verification", component: KycVerification },
      // Settings: order settings and OTP settings. /account/settings is the
      // address the header menu and older links use, so it opens the first.
      { path: "settings", redirect: "/account/settings/orders" },
      { path: "settings/orders", name: "OrderSettings", component: AccountSettings },
      { path: "settings/otp", name: "OtpSettings", component: OtpSettings },
    ],
  },
  // Earlier addresses of the Account pages. Kept as redirects: they are in
  // bookmarks and emails, and /kyc is the link the verification emails send.
  { path: "/profile", redirect: "/account/profile" },
  { path: "/change-password", redirect: "/account/password" },
  { path: "/business-profile", redirect: "/account/business" },
  { path: "/business", redirect: "/account/team" },
  { path: "/users", redirect: "/account/team" },
  { path: "/add_user", redirect: "/account/team/invite" },
  {
    path: "/edituser/:id",
    redirect: (to: any) => `/account/team/${to.params.id}/edit`,
  },
  { path: "/kyc", redirect: "/account/verification" },
  // Guided company verification: one document per step. Full page, without
  // the app header (App.vue focusRoutes).
  { path: "/verify", name: "VerifyFlow", component: VerifyFlow },
  // Unfinished pages, not linked from anywhere in the app: bulk invite has no
  // backend, and the invite link is a placeholder. Left as they were.
  {
    path: "/bulk_invite",
    name: "InviteMany",
    component: InviteMany,
  },
  {
    path: "/create_link",
    name: "CreateLink",
    component: CreateLink,
  },
  {
    path: "/transactions/payments",
    name: "ViewPayments",
    component: ViewPayments,
  },
  {
    path: "/transactions/orders",
    name: "OrderHistory",
    component: OrderHistory,
  },
  {
    path: "/transactions/statement",
    name: "ViewStatement",
    component: ViewStatement,
  },
  {
    path: "/request",
    name: "NewRequest",
    component: NewRequest,
  },
  {
    path: "/track/:id",
    name: "TrackOrder",
    component: TrackOrder,
  },
  {
    path: "/track",
    name: "TrackOrderQuery",
    component: TrackOrder,
  },
  {
    path: "/trip",
    name: "PublicTrip",
    component: PublicTrip,
  },
  {
    path: "/rate/:id",
    name: "RateOrder",
    component: RateOrder,
  },
  {
    path: "/rate",
    name: "RateOrderQuery",
    component: RateOrder,
  },
  {
    path: "/reset/:id",
    name: "ResetPassword",
    component: ResetPassword,
  },
  {
    path: "/reset",
    name: "ResetPasswordQuery",
    component: ResetPassword,
  },
  {
    path: "/onboard/:id",
    name: "InviteLanding",
    component: InviteLanding,
  },
  {
    path: "/onboard",
    name: "InviteLandingQuery",
    component: InviteLanding,
  },
  {
    path: "/terms",
    name: "Terms",
    component: Terms,
  },
  { path: "/", redirect: "/request" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("_panda");
  const publicPages = [
    "Signup",
    "Login",
    "ForgotPassword",
    "ResetPassword",
    "ResetPasswordQuery",
    "InviteLanding",
    "Terms",
    "PublicTrip",
  ];

  const authRequired = !publicPages.includes(String(to.name));
  const isAuthenticated = !!token;

  if (authRequired && !isAuthenticated) {
    // Carry the destination through sign-in, so a link from an email (the
    // verification emails link to /kyc) lands where it points.
    next(to.fullPath && to.fullPath !== "/" ? { path: "/login", query: { next: to.fullPath } } : "/login");
    return;
  }

  if (isAuthenticated && authRequired && isAdminOnlyPath(to.path)) {
    if (!isCompanyAdminFromToken(token)) {
      next("/request");
      return;
    }
  }

  next();

  const queryString = to.fullPath.split("?")[1];
  if (queryString) {
    to.path += `?${queryString}`;
  }
});

export default router;
